import { globalStyle, style } from "@vanilla-extract/css";

import { vars } from "~/styles/tokens.css";

// Two-column shell. The sidebar is fixed so long pages scroll under
// a stable nav; the main column owns its own scroll area and takes
// the remaining viewport width.
export const shell = style({
  minHeight: "100vh",
  backgroundColor: vars.color.background,
});

// ── Sidebar ────────────────────────────────────────────────────────────

const SIDEBAR_WIDTH = "240px";

export const sidebar = style({
  width: SIDEBAR_WIDTH,
  position: "fixed",
  top: 0,
  left: 0,
  bottom: 0,

  display: "flex",
  flexDirection: "column",

  backgroundColor: vars.color.sidebarBackground,
  color: vars.color.sidebarText,
  zIndex: vars.zIndex.sticky,

  // Right-edge drop shadow to lift the sidebar off the page content.
  // Subtle — enough to read as elevation without feeling heavy.
  boxShadow: "2px 0 8px rgba(0, 0, 0, 0.08)",

  overflowY: "auto",
});

export const sidebarLogo = style({
  paddingBlock: vars.space.lg,
  paddingInline: vars.space.md,
  borderBottomWidth: "1px",
  borderBottomStyle: "solid",
  borderBottomColor: "rgba(255, 255, 255, 0.08)",

  display: "flex",
  flexDirection: "column",
  gap: vars.space.sm,
});

export const sidebarLogoText = style({
  fontFamily: vars.font.heading,
  fontSize: vars.fontSize.lg,
  fontWeight: vars.fontWeight.bold,
  color: vars.color.sidebarText,
  letterSpacing: "0.04em",
  margin: 0,
  lineHeight: vars.lineHeight.tight,
});

export const sidebarLogoSub = style({
  fontSize: vars.fontSize.xs,
  fontWeight: vars.fontWeight.semibold,
  color: vars.color.sidebarAccent,
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  marginTop: vars.space.xs,
});

export const sidebarNav = style({
  flex: 1,
  paddingBlock: vars.space.md,
  display: "flex",
  flexDirection: "column",
  gap: vars.space.md,
});

export const navSection = style({
  display: "flex",
  flexDirection: "column",
});

export const navSectionLabel = style({
  fontSize: "0.625rem", // 10px
  fontWeight: vars.fontWeight.bold,
  color: "rgba(255, 255, 255, 0.4)",
  letterSpacing: "0.15em",
  textTransform: "uppercase",
  paddingInline: vars.space.md,
  marginBottom: vars.space.sm,
});

// Nav item. The border-left trick keeps horizontal position stable
// between the active and resting states — we always reserve 3px for
// the accent bar, it's just transparent when the item isn't active.
export const navItem = style({
  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,

  paddingBlock: vars.space.sm,
  paddingInline: vars.space.md,

  fontFamily: vars.font.body,
  fontSize: vars.fontSize.sm,
  fontWeight: vars.fontWeight.regular,
  color: "rgba(255, 255, 255, 0.72)",

  borderLeftWidth: "3px",
  borderLeftStyle: "solid",
  borderLeftColor: "transparent",

  textDecoration: "none",
  cursor: "pointer",
  transition: "background-color 120ms ease, color 120ms ease",

  selectors: {
    "&:hover": {
      backgroundColor: "rgba(255, 255, 255, 0.04)",
      color: vars.color.sidebarText,
      textDecoration: "none",
    },
    "&:focus-visible": {
      outline: "none",
      backgroundColor: "rgba(255, 255, 255, 0.08)",
      color: vars.color.sidebarText,
      // Override the global focus shadow with an inset-ish gold line
      // on the accent rail so the keyboard focus reads clearly on a
      // dark background without blowing out the sidebar edges.
      boxShadow: `inset 3px 0 0 ${vars.color.sidebarAccent}`,
    },
  },
});

export const navItemActive = style({
  backgroundColor: vars.color.sidebarAccentSoft,
  color: vars.color.sidebarAccent,
  fontWeight: vars.fontWeight.semibold,
  borderLeftColor: vars.color.sidebarAccent,

  selectors: {
    "&:hover": {
      backgroundColor: vars.color.sidebarAccentSoftHover,
      color: vars.color.sidebarAccent,
    },
  },
});

export const navItemIcon = style({
  lineHeight: 1,
  width: "1.125rem",
  height: "1.125rem",
  flexShrink: 0,
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
});

export const sidebarFooter = style({
  paddingBlock: vars.space.md,
  paddingInline: vars.space.md,
  borderTopWidth: "1px",
  borderTopStyle: "solid",
  borderTopColor: "rgba(255, 255, 255, 0.08)",

  display: "flex",
  alignItems: "center",
  gap: vars.space.sm,
});

export const sidebarFooterBody = style({
  display: "flex",
  flexDirection: "column",
  minWidth: 0,
  flex: 1,
});

export const sidebarFooterName = style({
  fontSize: vars.fontSize.sm,
  fontWeight: vars.fontWeight.semibold,
  color: vars.color.sidebarText,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const sidebarFooterMeta = style({
  fontSize: vars.fontSize.xs,
  color: "rgba(255, 255, 255, 0.5)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
});

export const sidebarFooterAction = style({
  appearance: "none",
  background: "transparent",
  border: "none",
  padding: vars.space.xs,
  borderRadius: vars.radius.sm,

  fontFamily: vars.font.body,
  fontSize: vars.fontSize.xs,
  color: "rgba(255, 255, 255, 0.6)",
  cursor: "pointer",

  selectors: {
    "&:hover": {
      color: vars.color.sidebarAccent,
    },
    "&:focus-visible": {
      outline: "none",
      color: vars.color.sidebarAccent,
      boxShadow: vars.shadow.focus,
    },
  },
});

// ── Main content column ────────────────────────────────────────────────

export const main = style({
  marginLeft: SIDEBAR_WIDTH,
  minHeight: "100vh",
  display: "flex",
  flexDirection: "column",
});

export const content = style({
  flex: 1,
  paddingBlock: vars.space.lg,
  paddingInline: vars.space.xl,
  maxWidth: "1400px",
  width: "100%",
  boxSizing: "border-box",
});

// Full-bleed variant for app-pane surfaces (the Alpha workspace): no cap,
// no padding — the module owns its own frame edge to edge. Document-like
// pages keep `content`'s 1400px reading measure; an operator console on a
// 1920px monitor should not (DESIGN.md §4 "wide-viewport first").
export const contentFullBleed = style({
  flex: 1,
  width: "100%",
  boxSizing: "border-box",
});

// Override the global <a> link rules for sidebar nav items — the
// sidebar is on a dark surface, so the primary-colored link color
// would be illegible and the underline-on-hover reads as busy noise.
globalStyle(`${navItem}, ${navItem}:hover, ${navItem}:focus-visible`, {
  textDecoration: "none",
});
